<template>
  <div class="welcome-container">
    <!-- 背景装饰元素 -->
    <div class="floating-element"></div>
    <div class="floating-element"></div>
    <div class="floating-element"></div>
    <div class="floating-element"></div>

    <div class="welcome-card">
      <!-- 头部区域 -->
      <div class="welcome-header">
        <div class="logo">
          <div class="logo-icon">
            <i class="icon-shield">🛡️</i>
          </div>
          <h1 class="welcome-title">欢迎使用嬉乐安宁</h1>
          <p class="welcome-subtitle">智能家庭安全设备管理平台</p>
        </div>
      </div>

      <!-- 功能介绍区域 -->
      <div class="features-section">
        <h2 class="section-title">平台特色功能</h2>
        <div class="features-grid">
          <div class="feature-item">
            <div class="feature-icon">🏠</div>
            <h3>智能家庭管理</h3>
            <p>统一管理家庭安全设备，实时监控家庭安全状态</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">🔔</div>
            <h3>实时告警通知</h3>
            <p>异常情况即时推送，第一时间掌握家庭动态</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">📊</div>
            <h3>健康数据分析</h3>
            <p>设备运行数据可视化，智能分析健康趋势</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">📍</div>
            <h3>位置追踪服务</h3>
            <p>关爱家人安全，实时了解家人位置信息</p>
          </div>
        </div>
      </div>

      <!-- 授权说明区域 -->
      <div class="auth-info">
        <div class="info-box">
          <el-icon class="info-icon"><InfoFilled /></el-icon>
          <div class="info-text">
            <p><strong>登录后可享受完整功能：</strong></p>
            <ul>
              <li>管理家庭安全设备</li>
              <li>接收实时告警通知</li>
              <li>查看健康数据分析</li>
              <li>使用位置追踪服务</li>
              <li>自定义家庭成员权限</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- 操作按钮区域 -->
      <div class="action-buttons">
        <el-button
          type="primary"
          size="large"
          @click="goToLogin"
          class="login-btn"
        >
          <el-icon class="btn-icon"><UserFilled /></el-icon>
          立即登录/注册
        </el-button>
        <el-button
          size="large"
          @click="explorePlatform"
          class="explore-btn"
        >
          <el-icon class="btn-icon"><View /></el-icon>
          先看看平台介绍
        </el-button>
      </div>

      <!-- 平台介绍展开区域 -->
      <transition name="fade">
        <div v-if="showIntro" class="platform-intro">
          <h3>关于嬉乐安宁</h3>
          <p>
            嬉乐安宁是一款专业的智能家庭安全设备管理平台，致力于为每个家庭提供全方位的安全保障。
            通过先进的物联网技术，我们将家庭中的各类安全设备连接在一起，实现统一管理和智能监控。
          </p>
          <p>
            无论您是在家中还是外出，都能通过手机随时了解家庭安全状况。我们的实时告警系统能够在第一时间
            通知您任何异常情况，让您和家人享受安心、便捷的智能生活体验。
          </p>
          <div class="intro-stats">
            <div class="stat-item">
              <div class="stat-number">10,000+</div>
              <div class="stat-label">家庭用户</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">50+</div>
              <div class="stat-label">设备类型</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">99.9%</div>
              <div class="stat-label">在线率</div>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- 页脚 -->
    <div class="welcome-footer">
      <p>&copy; 2024 嬉乐安宁 | 智能安全设备管理平台</p>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { InfoFilled, UserFilled, View } from '@element-plus/icons-vue'

export default {
  name: 'Welcome',
  components: {
    InfoFilled,
    UserFilled,
    View
  },
  setup() {
    const router = useRouter()
    const showIntro = ref(false)

    const goToLogin = () => {
      router.push('/login')
    }

    const explorePlatform = () => {
      showIntro.value = !showIntro.value
    }

    return {
      showIntro,
      goToLogin,
      explorePlatform
    }
  }
}
</script>

<style scoped>
.welcome-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #b3e5fc 0%, #e0f7fe 100%);
  position: relative;
  overflow: hidden;
  padding: 40px 20px;
}

/* 背景装饰 - 复用Login页面的样式 */
.welcome-container::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background:
    radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px),
    radial-gradient(circle, rgba(79, 172, 254, 0.2) 1px, transparent 1px);
  background-size: 60px 60px, 40px 40px;
  background-position: 0 0, 25px 25px;
  animation: backgroundMove 25s linear infinite;
}

.welcome-container::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 15% 85%, rgba(79, 172, 254, 0.25) 0%, rgba(79, 172, 254, 0.05) 40%, transparent 70%),
    radial-gradient(circle at 85% 15%, rgba(0, 242, 254, 0.25) 0%, rgba(0, 242, 254, 0.05) 40%, transparent 70%);
  animation: floatingBubbles 12s ease-in-out infinite;
  pointer-events: none;
}

@keyframes floatingBubbles {
  0%, 100% {
    transform: translateY(0px) scale(1);
    opacity: 0.8;
  }
  50% {
    transform: translateY(-20px) scale(0.8);
    opacity: 0.6;
  }
}

@keyframes backgroundMove {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

/* 浮动元素 */
.welcome-container .floating-element {
  position: absolute;
  width: 8px;
  height: 8px;
  background: rgba(79, 172, 254, 0.6);
  border-radius: 50%;
  pointer-events: none;
  box-shadow: 0 0 10px rgba(79, 172, 254, 0.3);
}

.welcome-container .floating-element:nth-child(1) {
  top: 10%;
  left: 20%;
  animation: float1 8s ease-in-out infinite;
}

.welcome-container .floating-element:nth-child(2) {
  top: 70%;
  right: 15%;
  animation: float2 10s ease-in-out infinite;
  background: rgba(0, 242, 254, 0.6);
  box-shadow: 0 0 10px rgba(0, 242, 254, 0.3);
  width: 10px;
  height: 10px;
}

.welcome-container .floating-element:nth-child(3) {
  bottom: 20%;
  left: 15%;
  animation: float3 12s ease-in-out infinite;
  background: rgba(255, 255, 255, 0.7);
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.4);
  width: 6px;
  height: 6px;
}

.welcome-container .floating-element:nth-child(4) {
  top: 30%;
  right: 30%;
  animation: float4 9s ease-in-out infinite;
  background: rgba(79, 172, 254, 0.5);
  box-shadow: 0 0 12px rgba(79, 172, 254, 0.3);
  width: 12px;
  height: 12px;
}

@keyframes float1 {
  0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.6; }
  50% { transform: translateY(-40px) translateX(20px); opacity: 1; }
}

@keyframes float2 {
  0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.6; }
  50% { transform: translateY(-30px) translateX(-20px); opacity: 1; }
}

@keyframes float3 {
  0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.7; }
  50% { transform: translateY(-50px) translateX(16px); opacity: 1; }
}

@keyframes float4 {
  0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.5; }
  50% { transform: translateY(-36px) translateX(24px); opacity: 1; }
}

/* 主卡片 */
.welcome-card {
  width: 900px;
  max-width: 95vw;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(20px);
  position: relative;
  z-index: 10;
  animation: cardSlideIn 0.8s ease-out;
  overflow: hidden;
}

@keyframes cardSlideIn {
  0% { transform: translateY(50px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}

/* 头部区域 */
.welcome-header {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  padding: 50px 40px;
  text-align: center;
  color: white;
  position: relative;
}

.logo-icon {
  font-size: 80px;
  margin-bottom: 20px;
  display: inline-block;
  animation: logoFloat 3s ease-in-out infinite;
}

@keyframes logoFloat {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

.welcome-title {
  margin: 0 0 12px 0;
  font-size: 42px;
  font-weight: 700;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  letter-spacing: 2px;
}

.welcome-subtitle {
  margin: 0;
  font-size: 20px;
  opacity: 0.95;
  font-weight: 300;
}

/* 功能介绍区域 */
.features-section {
  padding: 50px 40px;
}

.section-title {
  text-align: center;
  font-size: 28px;
  color: #2c3e50;
  margin-bottom: 40px;
  font-weight: 600;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
  margin-bottom: 20px;
}

.feature-item {
  text-align: center;
  padding: 30px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 16px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.feature-item:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 30px rgba(79, 172, 254, 0.2);
  border-color: #4facfe;
}

.feature-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.feature-item h3 {
  font-size: 18px;
  color: #2c3e50;
  margin-bottom: 10px;
  font-weight: 600;
}

.feature-item p {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

/* 授权说明区域 */
.auth-info {
  padding: 0 40px 30px;
}

.info-box {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 25px;
  background: linear-gradient(135deg, #e3f2fd 0%, #f3f8ff 100%);
  border-radius: 12px;
  border-left: 4px solid #4facfe;
}

.info-icon {
  font-size: 24px;
  color: #4facfe;
  flex-shrink: 0;
  margin-top: 2px;
}

.info-text {
  flex: 1;
}

.info-text p {
  margin: 0 0 10px 0;
  color: #2c3e50;
  font-size: 16px;
}

.info-text ul {
  margin: 10px 0 0 0;
  padding-left: 20px;
  list-style: none;
}

.info-text ul li {
  color: #555;
  font-size: 14px;
  line-height: 2;
  position: relative;
  padding-left: 20px;
}

.info-text ul li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #4facfe;
  font-weight: bold;
}

/* 操作按钮区域 */
.action-buttons {
  display: flex;
  gap: 20px;
  padding: 0 40px 40px;
  justify-content: center;
}

.login-btn,
.explore-btn {
  flex: 1;
  max-width: 280px;
  height: 56px;
  font-size: 18px;
  font-weight: 600;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.login-btn {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border: none;
  box-shadow: 0 4px 15px rgba(79, 172, 254, 0.3);
}

.login-btn:hover {
  background: linear-gradient(135deg, #4098fe 0%, #00d4fe 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(79, 172, 254, 0.4);
}

.explore-btn {
  background: white;
  border: 2px solid #4facfe;
  color: #4facfe;
}

.explore-btn:hover {
  background: #f0f9ff;
  border-color: #00d4fe;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(79, 172, 254, 0.2);
}

.btn-icon {
  font-size: 20px;
}

/* 平台介绍展开区域 */
.platform-intro {
  padding: 30px 40px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-top: 2px solid #e8f4fd;
  animation: slideDown 0.5s ease-out;
}

@keyframes slideDown {
  0% {
    max-height: 0;
    opacity: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
  100% {
    max-height: 500px;
    opacity: 1;
    padding-top: 30px;
    padding-bottom: 30px;
  }
}

.platform-intro h3 {
  font-size: 24px;
  color: #2c3e50;
  margin-bottom: 20px;
  font-weight: 600;
}

.platform-intro p {
  font-size: 16px;
  color: #555;
  line-height: 1.8;
  margin-bottom: 15px;
  text-align: justify;
}

.intro-stats {
  display: flex;
  justify-content: space-around;
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid #e0e0e0;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 36px;
  font-weight: 700;
  color: #4facfe;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  color: #666;
}

/* 页脚 */
.welcome-footer {
  position: relative;
  z-index: 10;
  margin-top: 30px;
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
}

.welcome-footer p {
  margin: 0;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* 过渡动画 */
.fade-enter-active, .fade-leave-active {
  transition: all 0.5s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .welcome-card {
    width: 95vw;
    margin: 20px 10px;
  }

  .welcome-header {
    padding: 40px 30px;
  }

  .welcome-title {
    font-size: 32px;
  }

  .welcome-subtitle {
    font-size: 16px;
  }

  .features-section {
    padding: 40px 30px;
  }

  .section-title {
    font-size: 24px;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .auth-info {
    padding: 0 30px 20px;
  }

  .action-buttons {
    flex-direction: column;
    padding: 0 30px 30px;
  }

  .login-btn,
  .explore-btn {
    max-width: 100%;
    width: 100%;
  }

  .platform-intro {
    padding: 25px 30px;
  }

  .intro-stats {
    flex-direction: column;
    gap: 20px;
  }
}
</style>
